import { findWhere, where, invert, first } from 'underscore';
import { getSmartPageDefaultSiteTheme } from '@bingads-webui-campaign-react/smart-page-site-theme';

import { WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING, WEBSITE_SOURCE_DATA_KEY_MAPPING } from './site-config-structure';

export const THEME_MODE_KEY = {
  Light: 'Light',
  Dark: 'Dark',
};

export const THEME_MODE_KEY_MAPPING = {
  Light: 'mode_light',
  Dark: 'mode_dark',
};

export const THEME_MODE_KEY_MAPPING_MT = invert(THEME_MODE_KEY_MAPPING);

export const THEME_ACCENT_COLOR_KEY_MAPPING = {
  Default: 'accent_color_default',
  Yellow: 'accent_color_yellow',
  Green: 'accent_color_green',
};

export const THEME_ACCENT_COLOR_KEY_MAPPING_MT = invert(THEME_ACCENT_COLOR_KEY_MAPPING);

export const smartPageThemeModeConfigList = [
  {
    id: 'mode_light',
    label: 'Light',
    color: '#FFFFFF',
  },
  {
    id: 'mode_dark',
    label: 'Dark',
    color: '#000000',
  },
];

export const smartPageAccentColorConfigList = [
  {
    id: 'accent_color_default',
    mode: 'mode_light',
    color: '#000000',
    label: 'Black',
  },
  {
    id: 'accent_color_default',
    mode: 'mode_dark',
    color: '#ffffff',
    label: 'White',
  },
  {
    id: 'accent_color_yellow',
    mode: 'mode_dark',
    color: '#fff500',
    label: 'Yellow',
  },
  {
    id: 'accent_color_green',
    mode: 'mode_dark',
    color: '#c4ffa8',
    label: 'Green',
  },
];

export const smartPageThemePaletteList = [
  {
    id: 'theme_light_default',
    mode: 'mode_light',
    accentColor: 'accent_color_default',
    primaryText: '#000000',
    secondaryText: '#000000',
    tertiaryText: '#605e5c',
    linkText: '#000000',
    primaryBackground: '#ffffff',
    secondaryBackground: '#f3f2f1',
    cardBackground: '#ffffff',
    outerBackground: '#edebe9',
    buttonText: '#ffffff',
    buttonBackground: '#323130',
    outline: '#000000',
    pageControl: '#605e5c',
    pageSelect: '#0078d4',
    footer: '#3e3e3e',
    elevation: 'rgba(0, 0, 0, 0.25)',
  },
  {
    id: 'theme_dark_default',
    mode: 'mode_dark',
    accentColor: 'accent_color_default',
    primaryText: '#ffffff',
    secondaryText: '#ffffff',
    tertiaryText: '#ffffff',
    linkText: '#ffffff',
    primaryBackground: '#323130',
    secondaryBackground: '#464646',
    cardBackground: '#464646',
    outerBackground: '#4c4c4c',
    buttonText: '#000000',
    buttonBackground: '#ffffff',
    outline: '#ffffff',
    pageControl: '#ffffff',
    pageSelect: '#ffffff',
    footer: '#ffffff',
    elevation: 'rgba(255, 255, 255, 0.25)',
  },
  {
    id: 'theme_dark_yellow',
    mode: 'mode_dark',
    accentColor: 'accent_color_yellow',
    primaryText: '#fff500',
    secondaryText: '#ffffff',
    tertiaryText: '#ffffff',
    linkText: '#ffffff',
    primaryBackground: '#323130',
    secondaryBackground: '#464646',
    cardBackground: '#464646',
    outerBackground: '#4c4c4c',
    buttonText: '#000000',
    buttonBackground: '#fff500',
    outline: '#ffffff',
    pageControl: '#ffffff',
    pageSelect: '#ffffff',
    footer: '#ffffff',
    elevation: 'rgba(255, 255, 255, 0.25)',
  },
  {
    id: 'theme_dark_green',
    mode: 'mode_dark',
    accentColor: 'accent_color_green',
    primaryText: '#c4ffa8',
    secondaryText: '#ffffff',
    tertiaryText: '#ffffff',
    linkText: '#ffffff',
    primaryBackground: '#323130',
    secondaryBackground: '#464646',
    cardBackground: '#464646',
    outerBackground: '#4c4c4c',
    buttonText: '#000000',
    buttonBackground: '#c4ffa8',
    outline: '#ffffff',
    pageControl: '#ffffff',
    pageSelect: '#ffffff',
    footer: '#ffffff',
    elevation: 'rgba(255, 255, 255, 0.25)',
  },
];

export function getSmartPageAccentColorConfigList(mode) {
  return where(smartPageAccentColorConfigList, { mode });
}

export const getThemePalette = (theme) => {
  if (theme) {
    const modeMT = theme[WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.Mode];
    const accentColorMT = theme[WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.AccentColor];
    if (modeMT && accentColorMT) {
      const mode = THEME_MODE_KEY_MAPPING[modeMT];
      const accentColor = THEME_ACCENT_COLOR_KEY_MAPPING[accentColorMT];
      const palette = findWhere(smartPageThemePaletteList, { mode, accentColor });
      if (palette) {
        return palette;
      }
    }
  }

  return first(smartPageThemePaletteList);
};

export const mapWebsiteSourceDataTheme = (siteConfig) => {
  if (siteConfig && siteConfig.websiteSourceData
    && siteConfig.websiteSourceData[WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme]) {
    const theme = siteConfig.websiteSourceData[WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme];

    const mode = theme.Mode ? THEME_MODE_KEY_MAPPING[theme.Mode] : undefined;
    const accentColor = theme.AccentColor ?
      THEME_ACCENT_COLOR_KEY_MAPPING[theme.AccentColor] : undefined;
    if (mode && accentColor) {
      return { mode, accentColor };
    }
  }

  const defaultThemePalette = first(smartPageThemePaletteList);
  return {
    mode: defaultThemePalette.mode,
    accentColor: defaultThemePalette.accentColor,
  };
};

export const getDefaultWebsiteSourceDataTheme = () => ({
  [WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.Mode]: THEME_MODE_KEY_MAPPING_MT.mode_light,
  [WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.AccentColor]:
    THEME_ACCENT_COLOR_KEY_MAPPING_MT.accent_color_default,
});

export const mapThemeToWebsiteSourceData = (mode, accentColor) => {
  if (mode && accentColor) {
    const modeMT = THEME_MODE_KEY_MAPPING_MT[mode];
    const accentColorMT = THEME_ACCENT_COLOR_KEY_MAPPING_MT[accentColor];

    if (modeMT && accentColorMT) {
      return {
        [WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.Mode]: modeMT,
        [WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.AccentColor]: accentColorMT,
      };
    }
  }

  return undefined;
};

export const getCurrentTheme = ({
  themeConfig,
  disableEvents = false,
  isMobile = false,
}) => {
  const currentPalette = getThemePalette(themeConfig);

  return getSmartPageDefaultSiteTheme(currentPalette, disableEvents, isMobile);
};

export const getThemeConfigFromWSD = wsd => (
  wsd
    ? wsd[WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme]
    : undefined
);

export const getIsDarkMode = (siteConfig) => {
  if (siteConfig
    && siteConfig.websiteSourceData
    && siteConfig.websiteSourceData[WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme]) {
    const theme = siteConfig.websiteSourceData[WEBSITE_SOURCE_DATA_KEY_MAPPING.Theme];
    const modeMT = theme[WEBSITE_SOURCE_DATA_THEME_KEY_MAPPING.Mode];
    return modeMT === THEME_MODE_KEY.Dark;
  }

  return false;
};
